import z from 'zod';
import { BusinessDataV2Schema } from '../account/business-data-v2';
import { BusinessUserV2Schema } from '../account/business-user-v2';
import { AllPaymentProvidersEnum, AllPaymentProviderSchema, } from '../common-tools/providers';
import { RecurringPlanClientSchema } from '../subscriptions/client';
import { BusinessDeviceStatusSchema, BusinessDeviceTypeSchema, } from './business-device';
import { AllPaymentTerminalsConnectionStatusSchema, AllPaymentTerminalsStatusSchema, PaymentTerminalExecutionModeSchema, } from './devices';
/** Selectable data slices for GET /api/v4/system/device-sync. */
export var DeviceSyncField;
(function (DeviceSyncField) {
    DeviceSyncField["APPLICATION_DEVICE_CONTEXT"] = "APPLICATION_DEVICE_CONTEXT";
    DeviceSyncField["BUSINESS_DATA"] = "BUSINESS_DATA";
    DeviceSyncField["USER_DATA"] = "USER_DATA";
    DeviceSyncField["TERMINALS"] = "TERMINALS";
    DeviceSyncField["BUSINESS_DEVICES"] = "BUSINESS_DEVICES";
    DeviceSyncField["PROVIDER_DETAILS"] = "PROVIDER_DETAILS";
    DeviceSyncField["PROVIDER_CREDENTIALS"] = "PROVIDER_CREDENTIALS";
    DeviceSyncField["RECURRING_PLANS"] = "RECURRING_PLANS";
})(DeviceSyncField || (DeviceSyncField = {}));
export const DeviceSyncFieldSchema = z.nativeEnum(DeviceSyncField);
/** Default sync intentionally excludes device-only context and payment credentials. */
export const DEFAULT_DEVICE_SYNC_FIELDS = [
    DeviceSyncField.BUSINESS_DATA,
    DeviceSyncField.USER_DATA,
    DeviceSyncField.TERMINALS,
    DeviceSyncField.BUSINESS_DEVICES,
    DeviceSyncField.PROVIDER_DETAILS,
    DeviceSyncField.RECURRING_PLANS,
];
const DEVICE_SYNC_FIELDS = new Set(Object.values(DeviceSyncField));
export const DeviceSyncFieldsSchema = z
    .string()
    .default(DEFAULT_DEVICE_SYNC_FIELDS.join(','))
    .transform((value, ctx) => {
    const fields = value
        .split(',')
        .map((field) => field.trim())
        .filter(Boolean);
    if (fields.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one device sync field is required',
        });
        return z.NEVER;
    }
    const invalidField = fields.find((field) => !DEVICE_SYNC_FIELDS.has(field));
    if (invalidField) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Unsupported device sync field: ${invalidField}`,
        });
        return z.NEVER;
    }
    return [...new Set(fields)];
});
export const DeviceSyncQuerySchema = z.object({
    fields: DeviceSyncFieldsSchema,
});
export const ApplicationDeviceContextSchema = z.object({
    accountNumber: z.string().optional(),
    businessId: z.string().uuid(),
    businessDeviceId: z.string().uuid(),
    businessLocationId: z.string().uuid().nullable(),
    terminalId: z.string().uuid().nullable().optional(),
    deviceFingerprint: z.string().min(1).nullable(),
    publicKeyFingerprint: z.string().min(1),
    deviceName: z.string().min(1).nullable().optional(),
    deviceType: BusinessDeviceTypeSchema,
    environment: z.string().min(1).optional(),
    lastResolvedAt: z.string().datetime().optional(),
});
/** Client response form; PIN verifiers are server-only. */
export const BusinessUserV2ClientSchema = BusinessUserV2Schema.omit({
    pinHash: true,
});
export const DeviceSyncTerminalSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    deviceTag: z.string(),
    /** Legacy terminal rows may contain provider values outside the canonical provider enum. */
    provider: z.string().min(1),
    providerTerminalId: z.string(),
    serialNumber: z.string().nullable(),
    status: AllPaymentTerminalsStatusSchema,
    connectionStatus: AllPaymentTerminalsConnectionStatusSchema,
    /** Legacy terminal rows may contain types outside the canonical terminal type enum. */
    type: z.string().min(1),
    executionMode: PaymentTerminalExecutionModeSchema,
    businessLocationId: z.string().uuid().nullable(),
    /** Provider-owned location identifier used when connecting local readers. */
    providerLocationId: z.string().min(1).nullable().optional(),
    /** Assigned BlockChyp terminal's configured LAN address hint; never provider credentials. */
    localIpAddress: z.string().ip({ version: 'v4' }).nullable().optional(),
});
export const DeviceSyncBusinessDeviceSchema = z.object({
    id: z.string().uuid(),
    businessId: z.string().uuid(),
    businessLocationId: z.string().uuid().nullable(),
    terminalId: z.string().uuid().nullable(),
    deviceName: z.string().min(1).nullable(),
    deviceType: BusinessDeviceTypeSchema,
    status: BusinessDeviceStatusSchema,
    serialNumber: z.string().nullable(),
    lastPairedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const DeviceSyncProviderCapabilitiesSchema = z.object({
    achPayments: z.boolean(),
    inPersonPayments: z.boolean(),
    onlinePayments: z.boolean(),
});
export const DeviceSyncProviderMetadataSchema = z.object({
    capabilities: DeviceSyncProviderCapabilitiesSchema.optional(),
    stripe: z
        .object({
        chargesEnabled: z.boolean().optional(),
        payoutsEnabled: z.boolean().optional(),
        locationId: z.string().min(1).optional(),
    })
        .optional(),
    blockChyp: z
        .object({
        merchantId: z.string().min(1).optional(),
        companyName: z.string().optional(),
        dbaName: z.string().optional(),
        status: z.string().optional(),
    })
        .optional(),
    tilled: z
        .object({
        merchantNumber: z.string().optional(),
        customerNumber: z.string().optional(),
        dbaNumber: z.string().optional(),
    })
        .optional(),
    payfactory: z
        .object({
        accountNumber: z.string().optional(),
    })
        .optional(),
    north: z
        .object({
        merchantNumber: z.string().optional(),
        customerNumber: z.string().optional(),
        dbaNumber: z.string().optional(),
    })
        .optional(),
});
export const DeviceSyncProviderDetailSchema = z.object({
    id: z.string().uuid(),
    provider: AllPaymentProviderSchema,
    providerAccountId: z.string().nullable(),
    enabled: z.boolean(),
    usesLocalTerminal: z.boolean(),
    metadata: DeviceSyncProviderMetadataSchema,
});
export const DeviceSyncProviderDetailsSchema = z.array(DeviceSyncProviderDetailSchema);
export const DeviceSyncBlockChypCredentialsSchema = z.object({
    apiKey: z.string().min(1),
    bearerToken: z.string().min(1),
    signingKey: z.string().min(1),
    tokenizingKey: z.string().min(1).optional(),
});
export const DeviceSyncProviderCredentialSchema = z.object({
    id: z.string().uuid(),
    provider: z.literal(AllPaymentProvidersEnum.BLOCKCHYP),
    credentials: DeviceSyncBlockChypCredentialsSchema,
    merchantInfo: z
        .object({
        merchantId: z.string().min(1),
        companyName: z.string().optional(),
        dbaName: z.string().optional(),
        status: z.string().optional(),
    })
        .optional(),
});
export const DeviceSyncProviderCredentialsSchema = z.array(DeviceSyncProviderCredentialSchema);
export const DeviceSyncResponseSchema = z.object({
    applicationDeviceContext: ApplicationDeviceContextSchema.optional(),
    businessData: BusinessDataV2Schema.optional(),
    userData: z.array(BusinessUserV2ClientSchema).optional(),
    terminals: z.array(DeviceSyncTerminalSchema).optional(),
    businessDevices: z.array(DeviceSyncBusinessDeviceSchema).optional(),
    providerDetails: DeviceSyncProviderDetailsSchema.optional(),
    providerCredentials: DeviceSyncProviderCredentialsSchema.optional(),
    recurringPlans: z.array(RecurringPlanClientSchema).optional(),
});
/** Human-authenticated Manager access to one selected terminal and provider account. */
export const ManagerPaymentTerminalContextSchema = z.object({
    terminal: DeviceSyncTerminalSchema,
    providerDetail: DeviceSyncProviderDetailSchema,
    credential: DeviceSyncProviderCredentialSchema.nullable(),
});
